import React, { useEffect, useState } from 'react';
import I18n from '../../blocks/localization';
import { useLocation } from 'react-router-dom';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import ContentLayout from '../core/ContentLayout';
import { ErrorMessage, Formik } from 'formik';
import TopUpEWalletRequest from '../../models/eWallet/TopUpEWalletRequest';
import { getCardInfoValidationSchema, topUpEWalletAnonymousValidationSchema, topUpEWalletValidationSchema } from '../../blocks/validation/validationSchema';
import { connect, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import PrometModal from '../shared/Modal';
import NumberUtility from '../../blocks/utils/number-utility';
import icon_e_novcanik from '../../assets/icons/2x/icon_e-novcanik.png';
import PrometBoxListSelect from '../shared/promet-box-list-select/PrometBoxListSelect';
import EWalletPredefinedValueUtility from '../../blocks/utils/eWallet-predefined-value-utility/EWalletPredefinedValueUtility';
import { topUpEWalletActions } from '../../store/e-wallet/actions';
import PrometInfoIconBox from '../shared/PrometInfoIconBox';
import EWalletUtility from '../../blocks/utils/e-wallet-utility/EWalletUtility';
import TermsOfUseContent from './TermsOfUseContent';
import Toast from '../shared/Toast';
import { getCardInfoActions } from '../../store/client/actions';
import HorizontalDivider from '../shared/HorizontalDivider';
import EuroUtility from '../../blocks/utils/euro-utility';
import CurrencyUtility from '../../blocks/utils/currency-utility/CurrencyUtility';
import EWalletConst from '../../blocks/consts/EWalletConst';

interface Location {
    topUpAmount?: number;
}

type Props = typeof mapDispatchToProps;

const EWallet = (props: Props) => {
    const [initialTopUpEWalletRequest, setInitialTopUpEWalletRequest] = useState<TopUpEWalletRequest>({ amount: undefined, cardNumber: '', email: '', areTermsOfUseAccepted: false });
    const [initialGetCardInfoRequest, setInitialGetCardInfoRequest] = useState({ cardSerialNumber: '' });
    const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = useState(false);

    const account = useSelector((state: ApplicationState) => state.account.account);
    const client = useSelector((state: ApplicationState) => state.client.client);
    const cardInfo = useSelector((state: ApplicationState) => state.client.cardInfo);

    const location = useLocation<Location>();

    const primaryCurrency = CurrencyUtility.getPrimary();
    const secondaryCurrency = CurrencyUtility.getSecondary();
    const maxEWalletAmount = EWalletConst.getMaxEWalletAmount();

    const ePurseAmount = EWalletUtility.ePurseAmount(account, client);

    const maxOtherAmount = EWalletPredefinedValueUtility.maxOtherAmount(ePurseAmount);

    const topUpAmount = location.state?.topUpAmount;

    useEffect(() => {
        setInitialTopUpEWalletRequest({
            ...initialTopUpEWalletRequest,
            amount: location.state?.topUpAmount,
        } as TopUpEWalletRequest);
    }, [topUpAmount]); // eslint-disable-line react-hooks/exhaustive-deps

    const termsOfUseModalClose = () => setIsTermsOfUseModalOpen(false);
    const termsOfUseModalOpen = () => setIsTermsOfUseModalOpen(true);

    const handleFormSubmit = (topUpEWalletRequest: TopUpEWalletRequest) => {
        if (account?.isFullProfileActivationInProgress) {
            Toast.notifyInfo(I18n.t('activateFullProfile.processStarted'));
            return;
        }
        props.topUpEWallet(topUpEWalletRequest);
    }

    const handleGetCardInfoFormSubmit = (getCardInfoRequest: any) => {
        props.getCardInfo(getCardInfoRequest.cardSerialNumber);
    }

    const getValidationSchema = () => {
        return account ? topUpEWalletValidationSchema : topUpEWalletAnonymousValidationSchema;
    }

    return (
        <ContentLayout titleTranslateId='eWallet.title'>
            <Row>
                <Col xl={12}>
                    <Formik
                        validationSchema={getValidationSchema()}
                        validateOnBlur
                        initialValues={initialTopUpEWalletRequest}
                        enableReinitialize
                        onSubmit={(values) => handleFormSubmit(values)}>
                        {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
                            <>
                                {
                                    !account &&
                                    <>
                                        <FormGroup>
                                            <Label>{I18n.t('eWallet.cardNumberLabel')}</Label>
                                            <Input type="text" name="cardNumber" onChange={handleChange} placeholder={I18n.t('eWallet.cardNumber')}
                                                className={errors.cardNumber && touched.cardNumber ? ' is-invalid' : ''} value={values.cardNumber} />
                                            <ErrorMessage name="cardNumber" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>{I18n.t('eWallet.emailLabel')}</Label>
                                            <Input type="text" name="email" onChange={handleChange} placeholder={I18n.t('eWallet.email')}
                                                className={errors.email && touched.email ? ' is-invalid' : ''} value={values.email} />
                                            <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </>
                                }
                                {
                                     account &&
                                    <PrometInfoIconBox
                                        icon={icon_e_novcanik}
                                        content={
                                            <div>
                                                <span>{I18n.t('eWallet.accountEWalletAmount')} </span>
                                                <span className='eWallet-top-up-account-amount'>{`${NumberUtility.format(ePurseAmount, 2, true, false, ',')} ${primaryCurrency.sign}`}</span>
                                                <span className='primary-color'>{` (${EuroUtility.convert(ePurseAmount)} ${secondaryCurrency.sign})`} </span><br />
                                                <span className='info-text-primary'>{I18n.t('eWallet.topUpInfoText', { primaryAmount: maxEWalletAmount, primarySign: primaryCurrency.sign, secondaryAmount: EuroUtility.convert(maxEWalletAmount), secondarySign: secondaryCurrency.sign })}</span>
                                            </div>
                                        }
                                    />
                                }

                                <Row>
                                    <Col xl={12}>
                                        <div className='mb-3'>
                                            {I18n.t('eWallet.amountLabel')}
                                        </div>

                                        <PrometBoxListSelect
                                            name="amount"
                                            options={EWalletPredefinedValueUtility.getAsDdl()}
                                            value={values.amount}
                                            onChanged={(name, id) => setFieldValue(name, id)}
                                            isDisabled={(id: number) => !!account && !EWalletPredefinedValueUtility.isBelowLimit(id, ePurseAmount)}
                                            maxOtherAmount={maxOtherAmount}
                                        />

                                        <input className={errors.amount && touched.amount ? ' is-invalid' : ''} style={{ display: 'none' }} />
                                        <ErrorMessage name="amount" component="div" className="invalid-feedback" />
                                    </Col>
                                </Row>

                                <Row className='mt-3'>
                                    <Col xl={12}>
                                        {I18n.t('eWallet.selectedAmount')}
                                        <span className='eWallet-selected-amount'>
                                            {`${NumberUtility.format(values.amount || 0, 2, true, false, ',')} ${primaryCurrency.sign}`}                                            
                                        </span>
                                        <span className='primary-color'>{` (${EuroUtility.convert(values.amount)} ${secondaryCurrency.sign})`} </span>

                                    </Col>
                                </Row>

                                <FormGroup className='mt-2'>
                                    <input type="checkbox" name="areTermsOfUseAccepted" onChange={handleChange}
                                        className={'mr-2' + (errors.areTermsOfUseAccepted && touched.areTermsOfUseAccepted ? ' is-invalid' : '')} />
                                    <span>{I18n.t('eWallet.termsOfUseLabel')}</span> &nbsp;
                                    <span className="text-decoration-underline cursor-pointer" onClick={termsOfUseModalOpen}>{I18n.t('findOutMore')}</span>
                                    <ErrorMessage name="areTermsOfUseAccepted" component="div" className="invalid-feedback" />
                                </FormGroup>

                                <Button type="button" color="primary" onClick={handleSubmit}>{I18n.t('eWallet.topUp')}</Button>
                            </>
                        )}
                    </Formik>
                </Col>
            </Row>

            <PrometModal
                isOpen={isTermsOfUseModalOpen}
                onOkClick={termsOfUseModalClose}
                onClose={termsOfUseModalClose}
                footer
                headerText={I18n.t('termsOfUse.title')}
                okText={I18n.t('close')}
                size='lg'
            >
                <TermsOfUseContent height='400px' />
            </PrometModal>

            {
                !account &&
                <>
                    <HorizontalDivider />

                    <h3>{I18n.t('eWallet.checkCardEWalletTitle')}</h3>
                    <Row>
                        <Col xl={12}>
                            <Formik
                                validationSchema={getCardInfoValidationSchema}
                                validateOnBlur
                                initialValues={initialGetCardInfoRequest}
                                enableReinitialize
                                onSubmit={(values) => handleGetCardInfoFormSubmit(values)}>
                                {({ handleChange, handleSubmit, values, errors, touched }) => (
                                    <>
                                        <FormGroup>
                                            <Label>{I18n.t('eWallet.checkCardEWalletLabel')}</Label>
                                            <Input type="text" name="cardSerialNumber" onChange={handleChange} placeholder={I18n.t('eWallet.cardNumber')}
                                                className={errors.cardSerialNumber && touched.cardSerialNumber ? ' is-invalid' : ''} value={values.cardSerialNumber} />
                                            <ErrorMessage name="cardSerialNumber" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                        {
                                            cardInfo && cardInfo.isCardActive &&
                                            <div className='mb-4'>
                                                <span>{I18n.t('eWallet.accountEWalletAmount')} </span>
                                                <span className='eWallet-top-up-account-amount'>{`${NumberUtility.format(cardInfo.ePurseAmount, 2, true, false, ',')} ${primaryCurrency.sign}`}</span><br />
                                            </div>
                                        }

                                        <Button type="button" color="primary" onClick={handleSubmit}>{I18n.t('eWallet.checkCardEWallet')}</Button>
                                    </>
                                )}
                            </Formik>
                        </Col>
                    </Row>
                </>
            }

        </ContentLayout>
    );
}

const mapDispatchToProps = {
    topUpEWallet: topUpEWalletActions.request,
    getCardInfo: getCardInfoActions.request
};

export default connect(null, mapDispatchToProps)(EWallet as any);
